// A godbolt-style read-only view of a compiled artifact: either the Core IR
// (the output of monomorphization) or the lowered Go source. Both come from the
// compiler as an `EmitResult` — code on success, or a message when the program
// doesn't compile or uses a construct the pass can't lower yet — so this one
// component renders both tabs. A copy button lifts the text out for inspection.

import { useState } from "react";
import type { EmitResult } from "../../src/compiler.js";

interface CompiledPanelProps {
  /** What this view shows, for the empty/label text ("Core", "Go"). */
  label: string;
  /** The lowering result, or null while nothing has been produced (no source). */
  result: EmitResult | null;
}

export function CompiledPanel({ label, result }: CompiledPanelProps) {
  const [copied, setCopied] = useState(false);

  if (!result) {
    return <div className="cv-empty">— nothing to show yet</div>;
  }
  if (result.error !== null) {
    return (
      <div className="cv-error">
        <div className="cv-error-head">can’t emit {label}</div>
        {result.error}
      </div>
    );
  }

  const code = result.code ?? "";
  const copy = (): void => {
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      () => {}
    );
  };

  return (
    <div className="codeview">
      <button className="cv-copy" onClick={copy} title={`Copy ${label} to clipboard`}>
        {copied ? "copied ✓" : "copy"}
      </button>
      <pre className="cv-pre">{code}</pre>
    </div>
  );
}
